import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import EmptyAddCardCst from "@components/EmptyAddCardCst";
import LanguageSelectorLinks from "@components/InputCst/LanguageSelector/Links";
import { WizardStepErrors } from "@components/WizardCst/types";
import { UsefulLink } from "../../../types";
import { useLoTranslationContext } from "../../../../hooks/useLoTranslation";
import UsefulLinkModalCst from "./UsefulLinkModalCst";

interface UsefulLinksFieldProps {
  values: UsefulLink[];
  onChange: (next: UsefulLink[]) => void;

  errors?: WizardStepErrors;
}

const UsefulLinksField: React.FC<UsefulLinksFieldProps> = ({
  values,
  onChange,
}) => {
  const { t } = useTranslation("wizard-activity");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const trans = useLoTranslationContext();
  const canTranslate = !!trans?.languages?.secondary_languages?.length;

  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleAdd = (link: UsefulLink) => {
    onChange([...values, link]);
  };

  const handleRemove = (idx: number) => {
    const next = values.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: theme.palette.text.primary,
          }}
        >
          {t("fields.useful-links")}
        </Typography>
        <Box
          onClick={handleOpenModal}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
            color: theme.palette.primary.main,
            fontSize: "13px",
            fontWeight: 600,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {t("common.add")}
          <Plus size={14} />
        </Box>
      </Box>

      {values.length === 0 ? (
        <EmptyAddCardCst
          addLabel={t("common.add") || "Aggiungi"}
          onAdd={handleOpenModal}
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {values.map((link, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                padding: "12px 16px",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {link.url_label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.text.tertiary,
                    lineHeight: 1.4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {[link.url, link.description].filter(Boolean).join(" | ")}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.7,
                  flexShrink: 0,
                  "& .MuiIconButton-root": { mr: 0 },
                }}
              >
                {canTranslate && link.id != null && trans && (
                  <LanguageSelectorLinks
                    languages={trans.languages}
                    col={{ accessorKey: "useful_links" }}
                    id={corporateId}
                    idObject={link.id}
                  />
                )}
                <Box
                  onClick={() => handleRemove(idx)}
                  sx={{
                    cursor: "pointer",
                    color: theme.palette.text.tertiary,
                    display: "flex",
                    "&:hover": { color: theme.palette.error.main },
                  }}
                >
                  <Trash2 size={20} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <UsefulLinkModalCst
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleAdd}
      />
    </Box>
  );
};

export default UsefulLinksField;
